"use client"

import React from "react"
import { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchSearchedCandidatesAsync,
  selectSearchedCandidates,
  selectSearchPagination,
  selectSearchLoading,
  resetSearchResults,
} from "./Candidate/CandidateSlice/CandidateSearch/CandidateSearchSlice"
import { RESUME_VIEW } from "../../common/URLs"
import { UnlockCandidateAsync } from "../employerDashboardSlice/UnlockCandidate/UnlockCandidateSlice"
import { useToast } from "@chakra-ui/react"
import {
  addEmployerSavedCandidateAsync,
  addSavedCandidateUuid,
  removeEmployerSavedCandidateAsync,
  removeSavedCandidateUuid,
  selectSavedCandidateUuids,
} from "./Candidate/CandidateSlice/EmployerSaveCandidate/EmployerSaveCandidateSlice"

const SEARCH_CRITERIA_KEY = "candidateSearchCriteria"
const SKILL_CHIP_LIMIT = 6 // limit chips to keep card heights consistent

const ApplicantCard = () => {
  const [unlockStatus, setUnlockStatus] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [searchCriteriaState, setSearchCriteriaState] = useState(null)
  const [hasInitialLoad, setHasInitialLoad] = useState(false)
  const { jobUuid } = useParams()
  const [bookmarkingCandidates, setBookmarkingCandidates] = useState(new Set())

  const location = useLocation()
  const navigate = useNavigate()
  const toast = useToast()
  const dispatch = useDispatch()

  // Get search criteria from location state or localStorage
  const searchCriteria =
    location.state || JSON.parse(localStorage.getItem(SEARCH_CRITERIA_KEY)) || null

  const candidates = useSelector(selectSearchedCandidates)
  const pagination = useSelector(selectSearchPagination)
  const loading = useSelector(selectSearchLoading)
  const savedCandidateUuids = useSelector(selectSavedCandidateUuids)

  // Save search criteria to localStorage when it changes
  useEffect(() => {
    if (searchCriteria) {
      localStorage.setItem(SEARCH_CRITERIA_KEY, JSON.stringify(searchCriteria))
    }
  }, [searchCriteria])

  // Initialize search criteria on component mount
  useEffect(() => {
    if (!hasInitialLoad) {
      if (location.state) {
        setSearchCriteriaState(location.state)
        setHasInitialLoad(true)
      } else {
        const savedCriteria = localStorage.getItem(SEARCH_CRITERIA_KEY)
        if (savedCriteria) {
          setSearchCriteriaState(JSON.parse(savedCriteria))
          setHasInitialLoad(true)
        }
      }
    }
  }, [location.state, hasInitialLoad])

  // Fetch candidates when search criteria or page changes
  useEffect(() => {
    if (searchCriteriaState && hasInitialLoad) {
      dispatch(
        fetchSearchedCandidatesAsync({
          ...searchCriteriaState,
          page: currentPage,
        })
      )
    }
  }, [dispatch, searchCriteriaState, currentPage, hasInitialLoad])

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const savedCriteria = localStorage.getItem(SEARCH_CRITERIA_KEY)
      if (savedCriteria) {
        setSearchCriteriaState(JSON.parse(savedCriteria))
      }
    }

    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  const clearSearchCriteria = () => {
    localStorage.removeItem(SEARCH_CRITERIA_KEY)
    setSearchCriteriaState(null)
    setHasInitialLoad(false)
    dispatch(resetSearchResults())
    navigate("/employer-dashboard/search-candidate")
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const openPdf = (pdfUrl) => {
    if (!pdfUrl || !pdfUrl.endsWith(".pdf")) {
      toast({
        title: "Error",
        description: "Invalid PDF file",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      return
    }
    window.open(pdfUrl, "_blank")
  }

  const handleUnlockCandidate = (uuid) => {
    if (!uuid) {
      toast({
        title: "Error",
        description: "Candidate UUID missing!",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      return
    }

    setUnlockStatus((prev) => ({
      ...prev,
      [uuid]: { status: "pending", message: "Unlocking..." },
    }))

    dispatch(UnlockCandidateAsync({ uuid }))
      .unwrap()
      .then(() => {
        setUnlockStatus((prev) => ({
          ...prev,
          [uuid]: { status: "success", message: "Unlocked" },
        }))

        toast({
          title: "Success",
          description: "Candidate unlocked successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      })
      .catch((error) => {
        setUnlockStatus((prev) => ({
          ...prev,
          [uuid]: { status: "error", message: "Failed to unlock" },
        }))

        const errorMessage = error.message || "Please Check your Subscription"
        if (errorMessage.includes("User not have valid subscription")) {
          toast({
            title: "Subscription Required",
            description: "User not have valid subscription!",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          })
        } else {
          toast({
            title: "Error",
            description: `Failed to unlock candidate: ${errorMessage}`,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          })
        }
      })
  }

  const isCandidateSaved = (candidateUuid) => {
    return savedCandidateUuids.includes(candidateUuid)
  }

  const isCandidateUnlocked = (candidate) => {
    return (
      candidate.is_candidate_unlock === true ||
      unlockStatus[candidate.candidate_uuid]?.status === "success"
    )
  }

  const handleApplicantClick = (candidate) => {
    const url = `/employer-dashboard/show-candidate/${candidate.candidate_uuid}`
    window.open(url, "_blank")
  }

  const isCandidateBeingBookmarked = (candidateUuid) => {
    return bookmarkingCandidates.has(candidateUuid)
  }

  // Toggle bookmark functionality - save or unsave candidate
  const handleToggleBookmark = async (candidateUuid, event) => {
    event.stopPropagation()

    if (isCandidateBeingBookmarked(candidateUuid)) {
      return
    }

    const isCurrentlySaved = isCandidateSaved(candidateUuid)
    setBookmarkingCandidates((prev) => new Set(prev).add(candidateUuid))

    try {
      if (isCurrentlySaved) {
        await dispatch(
          removeEmployerSavedCandidateAsync({ candidate_uuid: candidateUuid })
        ).unwrap()
        dispatch(removeSavedCandidateUuid(candidateUuid))
        toast({
          title: "Candidate removed",
          description: "The candidate has been removed from your saved list",
          status: "info",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } else {
        await dispatch(
          addEmployerSavedCandidateAsync({ candidate_uuid: candidateUuid })
        ).unwrap()
        dispatch(addSavedCandidateUuid(candidateUuid))
        toast({
          title: "Candidate saved",
          description: "The candidate has been added to your saved list",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      }
    } catch (error) {
      const action = isCurrentlySaved ? "remove" : "save"
      toast({
        title: `Failed to ${action} candidate`,
        description:
          error?.message ||
          `An error occurred while ${action === "remove" ? "removing" : "saving"
          } the candidate`,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setBookmarkingCandidates((prev) => {
        const newSet = new Set(prev)
        newSet.delete(candidateUuid)
        return newSet
      })
    }
  }

  const PaginationComponent = ({
    currentPage,
    lastPage,
    total,
    onPageChange,
    loading = false,
  }) => {
    const getVisiblePages = () => {
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (
        let i = Math.max(2, currentPage - delta);
        i <= Math.min(lastPage - 1, currentPage + delta);
        i++
      ) {
        range.push(i)
      }

      if (currentPage - delta > 2) {
        rangeWithDots.push(1, "...")
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (currentPage + delta < lastPage - 1) {
        rangeWithDots.push("...", lastPage)
      } else if (lastPage > 1) {
        rangeWithDots.push(lastPage)
      }

      return rangeWithDots
    }

    if (lastPage <= 1) return null

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 p-4 bg-white rounded-lg shadow-sm">
        <div className="text-sm text-gray-600">
          Showing page {currentPage} of {lastPage} ({total} total candidates)
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex items-center gap-1">
            {getVisiblePages().map((page, index) => (
              <React.Fragment key={index}>
                {page === "..." ? (
                  <span className="px-3 py-2 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => onPageChange(page)}
                    disabled={loading}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === page
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= lastPage || loading}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    )
  }

  if (loading && currentPage === 1) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-mainBg">
        <div className="text-center p-8 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <h2 className="mt-4 text-xl font-semibold text-gray-700">Loading candidates...</h2>
        </div>
      </div>
    )
  }

  if (!candidates || candidates.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-mainBg">
        <div className="text-center p-8 rounded-lg shadow-md">
          <h2 className="mt-4 text-xl font-semibold text-gray-700">No candidates found</h2>
          <p className="mt-2 text-gray-500">Try adjusting your search criteria</p>
          <button
            onClick={clearSearchCriteria}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Clear Search
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="">
      <div className="bg-mainBg p-3">
        <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
          <div className="flex justify-between items-center mb-6 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Results for the Search</h1>
              <p className="text-gray-500 mt-1">
                Found {pagination.total} candidate(s) matching your criteria
                {pagination.total > 0 && (
                  <span>
                    {" "}
                    - Page {pagination.current_page} of {pagination.last_page}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={clearSearchCriteria}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Clear Search
            </button>
          </div>

          {loading && currentPage > 1 && (
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Make all cards equal height per row via auto-rows-fr + h-full on cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 auto-rows-fr gap-6">
            {candidates?.map((candidate) => {
              const currentSkills = candidate.skills || []
              const preferredSkills = candidate.preference_skills || []
              const visibleCurrent = currentSkills.slice(0, SKILL_CHIP_LIMIT)
              const visiblePreferred = preferredSkills.slice(0, SKILL_CHIP_LIMIT)
              const moreCurrent = Math.max(currentSkills.length - SKILL_CHIP_LIMIT, 0)
              const morePreferred = Math.max(preferredSkills.length - SKILL_CHIP_LIMIT, 0)

              return (
                <div
                  key={candidate.id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden h-full flex flex-col"
                >
                  <div className="p-4 flex flex-col h-full">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between border-b mb-3 pb-2">
                      <div className="flex justify-between flex-wrap w-full min-w-0">
                        <a
                          href={`/employer-dashboard/show-candidate/${candidate.candidate_uuid}`}
                          className="hover:text-blue-600 transition-colors duration-200 block min-w-0"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <h2 className="text-xl font-semibold capitalize text-gray-800 truncate">
                            {candidate.candidate_name}
                          </h2>
                          <p className="text-gray-500 mt-1 clamp-2">
                            {(candidate.candidate_title || "Title not mentioned")}
                            {candidate.candidate_company && <span>, {candidate.candidate_company}</span>}
                            {candidate.candidate_location && <span>, {candidate.candidate_location}</span>}
                          </p>
                        </a>
                        <div className="mt-2 sm:mt-0 sm:ml-2">
                          <button
                            className={`action-btn-detailed ${isCandidateSaved(candidate.candidate_uuid) ? "btn-green" : "btn-gray"}`}
                            onClick={(e) => handleToggleBookmark(candidate.candidate_uuid, e)}
                            title={isCandidateSaved(candidate.candidate_uuid) ? "Remove from saved" : "Save candidate"}
                          >
                            {isCandidateBeingBookmarked(candidate.candidate_uuid) ? (
                              <i className="fa-solid fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fa-solid fa-bookmark"></i>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Body grows to fill, keeping buttons aligned bottom */}
                    <div className="space-y-4 flex-1">
                      {/* Meta grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <div>
                          <span className="font-medium text-gray-800">Current City: </span>
                          <span className="text-gray-600">
                            {candidate.candidate_current_city || "Not specified"}
                          </span>
                        </div>

                        <div>
                          <span className="font-medium text-gray-800">Gender: </span>
                          <span className="text-gray-600">
                            {candidate.candidate_gender || "Not specified"}
                          </span>
                        </div>

                        <div>
                          <span className="font-medium text-gray-800">Experience: </span>
                          <span className="text-gray-600">
                            {candidate.candidate_experience_year ? `${candidate.candidate_experience_year} Years` : ""}
                            {candidate.candidate_experience_year && candidate.candidate_experience_month ? ", " : ""}
                            {candidate.candidate_experience_month ? `${candidate.candidate_experience_month} Months` : ""}
                            {!candidate.candidate_experience_year && !candidate.candidate_experience_month && "Not specified"}
                          </span>
                        </div>

                        <div>
                          <span className="font-medium text-gray-800">Work Status: </span>
                          <span className="text-gray-600">
                            {candidate.candidate_work_status || "Not specified"}
                          </span>
                        </div>

                        <div>
                          <span className="font-medium text-gray-800">Notice Period: </span>
                          <span className="text-gray-600">
                            {candidate.candidate_notice_period || "Not specified"}
                          </span>
                        </div>
                      </div>

                      {/* Current Skills */}
                      {currentSkills.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-800">Current Skills: </span>
                          <div className="mt-0.5 flex flex-wrap">
                            {visibleCurrent.map((skillItem, index) => (
                              <span
                                key={index}
                                className="inline-block bg-green-50 text-green-700 px-2 py-1 rounded-full text-sm mr-2 mb-1"
                              >
                                {skillItem?.skill?.title || "N/A"}
                              </span>
                            ))}
                            {moreCurrent > 0 && (
                              <span className="inline-block text-gray-500 text-sm mr-2 mb-1">
                                +{moreCurrent} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Preferred Skills */}
                      <div>
                        <span className="font-medium text-gray-800">Preferred Skills: </span>
                        <div className="mt-0.5 flex flex-wrap">
                          {visiblePreferred.length > 0 ? (
                            <>
                              {visiblePreferred.map((skill, index) => (
                                <span
                                  key={index}
                                  className="inline-block bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-sm mr-2 mb-1"
                                >
                                  {skill?.skill?.title || "N/A"}
                                </span>
                              ))}
                              {morePreferred > 0 && (
                                <span className="inline-block text-gray-500 text-sm mr-2 mb-1">
                                  +{morePreferred} more
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">No preferred skills listed</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Footer anchored to bottom for consistent alignment */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mt-4 pt-3 border-t">
                      <div>
                        <span className="font-medium text-gray-800">Resume Status: </span>
                        <span className="text-gray-600">
                          {candidate.candidate_resume ? "Available" : "Not uploaded"}
                          {candidate.is_new_resume === 1 && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                              New Resume
                            </span>
                          )}
                        </span>
                      </div>

                      <div className="flex gap-2 flex-wrap justify-start sm:justify-end">
                        {isCandidateUnlocked(candidate) ? (
                          <>
                            {candidate.candidate_resume && (
                              <button
                                className="bg-green-600 hover:bg-green-700 text-white font-medium py-1.5 px-3 rounded-lg transition duration-300 ease-in-out"
                                onClick={() => openPdf(`${RESUME_VIEW}${candidate.candidate_resume}`)}
                              >
                                Open Resume
                              </button>
                            )}
                            <button
                              className="bg-green-600 hover:bg-green-700 text-white font-medium py-1.5 px-3 rounded-lg transition duration-300 ease-in-out"
                              onClick={() => handleApplicantClick(candidate)}
                            >
                              View Details
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleUnlockCandidate(candidate.candidate_uuid)}
                            className={`text-white font-medium py-1.5 px-4 rounded-lg transition duration-300 ease-in-out ${unlockStatus[candidate.candidate_uuid]?.status === "pending"
                              ? "bg-gray-400"
                              : unlockStatus[candidate.candidate_uuid]?.status === "error"
                                ? "bg-red-600 hover:bg-red-700"
                                : "bg-blue-600 hover:bg-blue-700"
                              }`}
                            disabled={unlockStatus[candidate.candidate_uuid]?.status === "pending"}
                          >
                            {unlockStatus[candidate.candidate_uuid]?.message || "Unlock Candidate"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            total={pagination.total}
            onPageChange={handlePageChange}
            loading={loading}
          />
        </div>
      </div>
      <style jsx>
        {`
          .action-btn-detailed {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            border: 1px solid;
            background: white;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            transition: all 0.2s ease;
          }

          .btn-green {
            border-color: #10b981;
            color: #10b981;
          }

          .btn-green:hover {
            background-color: #ecfdf5;
          }

          .btn-red {
            border-color: #ef4444;
            color: #ef4444;
          }

          .btn-red:hover {
            background-color: #fef2f2;
          }

          .btn-gray {
            border-color: #9ca3af;
            color: #9ca3af;
          }

          .btn-gray:hover {
            background-color: #f9fafb;
          }

          /* Basic multi-line clamp helpers without Tailwind plugin */
          .clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}
      </style>
    </div>
  )
}

export default ApplicantCard